import type { FC, ReactNode } from 'react'

interface ObservationHudProps {
    className?: string
}

const round = (value: number): number => Math.round(value * 100) / 100

const polar = (radius: number, degrees: number): [number, number] => {
    const angle = (degrees * Math.PI) / 180

    return [round(300 + Math.cos(angle) * radius), round(300 + Math.sin(angle) * radius)]
}

const arc = (radius: number, from: number, to: number): string => {
    const [x1, y1] = polar(radius, from)
    const [x2, y2] = polar(radius, to)

    return `M${x1} ${y1}A${radius} ${radius} 0 0 1 ${x2} ${y2}`
}

const TICKS = Array.from({ length: 120 }, (_, index) => {
    const length = index % 10 === 0 ? 16 : index % 5 === 0 ? 10 : 5
    const [x1, y1] = polar(292, index * 3)
    const [x2, y2] = polar(292 - length, index * 3)

    return { x1, y1, x2, y2, strong: index % 10 === 0 }
})

const ARCS = [
    arc(204, 8, 74),
    arc(204, 98, 160),
    arc(204, 188, 252),
    arc(204, 280, 340)
]

const LABELS = [
    { x: 300, y: 24, text: 'N 000' },
    { x: 578, y: 304, text: 'E 090' },
    { x: 300, y: 588, text: 'S 180' },
    { x: 22, y: 304, text: 'W 270' }
]

// Concentric observation rings: the outer ring is turned by scroll, the inner one drifts on its own.
const ObservationHud: FC<ObservationHudProps> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 600 600' fill='none' aria-hidden='true' focusable='false' className={className}>
            <circle cx='300' cy='300' r='292' stroke='currentColor' strokeOpacity='0.2' />

            <g data-hud-ring='outer'>
                {TICKS.map((tick, index) => (
                    <line
                        key={index}
                        x1={tick.x1}
                        y1={tick.y1}
                        x2={tick.x2}
                        y2={tick.y2}
                        stroke='currentColor'
                        strokeOpacity={tick.strong ? 0.55 : 0.26}
                    />
                ))}
            </g>

            <circle cx='300' cy='300' r='240' stroke='currentColor' strokeOpacity='0.3' strokeDasharray='2 7' />

            <g data-hud-ring='inner'>
                <g className='motion-safe:animate-spin-slow' style={{ transformBox: 'view-box', transformOrigin: 'center' }}>
                    {ARCS.map(path => (
                        <path key={path} d={path} stroke='currentColor' strokeOpacity='0.42' strokeWidth='1.5' />
                    ))}
                </g>
            </g>

            <circle cx='300' cy='300' r='140' stroke='currentColor' strokeOpacity='0.16' />
            <path d='M300 44V236M300 364V556M44 300H236M364 300H556' stroke='currentColor' strokeOpacity='0.22' />
            <circle cx='300' cy='300' r='3.5' fill='currentColor' fillOpacity='0.45' />

            {LABELS.map(label => (
                <text
                    key={label.text}
                    x={label.x}
                    y={label.y}
                    textAnchor='middle'
                    fontSize='11'
                    letterSpacing='3'
                    fill='currentColor'
                    fillOpacity='0.55'
                    className='font-mono'
                >
                    {label.text}
                </text>
            ))}
        </svg>
    )
}

export default ObservationHud
